import type { Logger } from "@expert/logging";
import { useMemo } from "react";
import { useOutageInfoStore } from "../store";

export interface OutageAlert {
    id: string;
    serviceName: string;
    timeAgo: string;
    reportCount: number;
    read: boolean;
    status: "active" | "clear";
}

export const useOutageNotifications = (logger?: Logger) => {
    const { getAllVisibleOutages, getUnreadOutages, markOutageAsRead, markAllOutagesAsRead } = useOutageInfoStore();

    const visibleOutages = getAllVisibleOutages();

    const outageAlerts = useMemo(
        () =>
            visibleOutages.map((outage) => ({
                id: outage.slug,
                serviceName: outage.slug,
                timeAgo: formatTimeAgo(outage.start_time, logger),
                reportCount: outage.total_reports,
                read: outage.isRead,
                status: outage.displayStatus,
            })),
        [visibleOutages, logger],
    );

    const unreadCount = getUnreadOutages().length;

    return {
        outageAlerts,
        unreadCount,
        markAsRead: markOutageAsRead,
        markAllAsRead: markAllOutagesAsRead,
    };
};

function formatTimeAgo(dateString: string, logger?: Logger): string {
    try {
        const utcDateString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
        const date = new Date(utcDateString);

        if (isNaN(date.getTime())) {
            throw new Error("Invalid date");
        }

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins} min ago`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours} hr ago`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    } catch (error) {
        logger?.error({ error, dateString }, "Outages | Error formatting outage time");
        return "Unknown time";
    }
}
